/**
 * useAvailableTaskTypes — Tipos de tarefa que o usuário pode CRIAR
 * -----------------------------------------------------------------------------
 * O módulo de Tarefas atende três tipos (task_type_enum): normal | retention | legal.
 * Nos atalhos de criação rápida (botão "+" do header, FAB do chat, painel de
 * atalhos) o modal precisa oferecer a escolha do tipo — porém apenas dos tipos
 * que o usuário realmente pode acessar.
 *
 * Regra de disponibilidade (cruzamento de duas camadas):
 *   1. Flag do tenant: tasks_enabled / retention_enabled / legal_enabled;
 *   2. Permissão de criação do menu correspondente:
 *        normal    -> 'n'               (Listagem Tarefas)
 *        retention -> 'retention_list'  (Listagem Retenção)
 *        legal     -> 'legal_list'      (Listagem Jurídico)
 *      Admin (root_admin / tenant_admin) tem bypass, conforme o contexto de
 *      permissões já resolve internamente.
 *
 * IMPORTANTE: este filtro é de USABILIDADE. A validação real continua
 * server-side nas RPCs (create_task) e nas permissões por fase.
 *
 * @version 1.2.0
 * RELEASE: 2026-08-22 v1.2.0 - Correção: as flags de Retenção/Jurídico passam a
 *          vir de `useTasksModuleSettings` (RPC get_tasks_module_settings), a
 *          mesma fonte usada pelo header. Antes eram lidas de `currentTenant`,
 *          que NÃO carrega `retention_enabled`/`legal_enabled` — o que fazia os
 *          dois tipos sumirem sempre (chips da pesquisa global e atalhos).
 * RELEASE: 2026-08-22 v1.1.0 - Novo parâmetro de intenção ('create' | 'view').
 *          No modo 'view' a checagem usa canView() do menu de listagem, para
 *          cenários de LEITURA (ex.: pesquisa global). Padrão continua 'create'
 *          para não alterar o comportamento dos atalhos já existentes.
 * RELEASE: 2026-08-22 v1.0.0 - Criação (seletor de tipo nos atalhos de tarefa)
 */

import { useMemo } from 'react';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuth } from '@/hooks/useAuth';
import { TASK_MODULES, type TaskModuleType } from '@/hooks/useTaskModuleType';
// v1.2.0: fonte única das flags dos submódulos (Retenção/Jurídico)
import { useTasksModuleSettings } from '@/hooks/useTasksModuleSettings';


/** Mapa fixo tipo -> chave de permissão de menu */
const TYPE_MENU_KEY: Record<TaskModuleType, string> = {
  normal: 'n',
  retention: 'retention_list',
  legal: 'legal_list',
};

/** v1.1.0: intenção do consumidor (criar tarefa x apenas visualizar/pesquisar) */
export type TaskTypeIntent = 'create' | 'view';

export interface AvailableTaskTypes {
  /** Tipos habilitados e permitidos, na ordem normal > retenção > jurídico */
  types: TaskModuleType[];
  /** true quando faz sentido exibir o seletor (mais de uma opção) */
  showSelector: boolean;
  /** Ainda carregando permissões (evita piscar o seletor) */
  isLoading: boolean;
}

/**
 * Retorna os tipos de tarefa disponíveis para o usuário atual.
 *
 * @param intent 'create' (padrão) valida permissão de criação;
 *               'view' valida apenas permissão de visualização.
 */
export function useAvailableTaskTypes(intent: TaskTypeIntent = 'create'): AvailableTaskTypes {
  const { currentTenant } = useAuth();
  const { canCreate, canView, isAdmin, loading } = usePermissions();
  // v1.2.0: flags reais dos submódulos (mesma fonte do header/menu lateral)
  const {
    retentionEnabled,
    legalEnabled,
    isLoading: settingsLoading,
  } = useTasksModuleSettings();

  return useMemo(() => {
    // Flags do tenant (defensivo: se não carregou, considera OFF).
    const tasksEnabled = currentTenant?.tasks_enabled ?? false;
    const enabledByType: Record<TaskModuleType, boolean> = {
      normal: tasksEnabled,
      retention: tasksEnabled && retentionEnabled,
      legal: tasksEnabled && legalEnabled,
    };

    const types = (Object.keys(TASK_MODULES) as TaskModuleType[]).filter((t) => {
      if (!enabledByType[t]) return false;
      if (isAdmin) return true;
      // v1.1.0: 'view' -> canView (leitura); 'create' -> canCreate (criação)
      return intent === 'view'
        ? canView(TYPE_MENU_KEY[t])
        : canCreate(TYPE_MENU_KEY[t]);
    });

    return {
      types,
      showSelector: types.length > 1,
      // v1.2.0: considera também o carregamento das flags dos submódulos
      isLoading: loading || settingsLoading,
    };
  }, [
    currentTenant,
    canCreate,
    canView,
    isAdmin,
    loading,
    intent,
    retentionEnabled,
    legalEnabled,
    settingsLoading,
  ]);

}

